import { AlertTriangle, X, ExternalLink } from 'lucide-react';
import { numEs } from '../../lib/utils';
import { parseTimestamp } from '../../lib/freshness';
import { riverLevel } from '../../lib/meteo';

// SAIH's level names. The API's keys are colours (amarillo/naranja/rojo) and "normalidad"; people
// used to read them raw: «— de normalidad a amarillo».
const BANNER_STYLE = {
  amarillo: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', icon: 'text-amber-600' },
  naranja: { bg: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-800', icon: 'text-orange-600' },
  rojo: { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-800', icon: 'text-red-600' },
};

export function RiverAlertBanner({ riverAlerts, dismissedAlerts, setDismissedAlerts }) {
  const now = Date.now();
  // An alert past its expiry is history, not a warning
  const activeAlerts = (riverAlerts || []).filter(a => {
    if (dismissedAlerts.includes(a.created_at)) return false;
    const exp = parseTimestamp(a.expires_at, now);
    return exp == null || exp > now;
  });
  if (activeAlerts.length === 0) return null;

  const topAlert = activeAlerts[0];
  const level = riverLevel(topAlert.overall_severity || 'amarillo');
  const cfg = BANNER_STYLE[level.key] || BANNER_STYLE.amarillo;

  return (
    <div className={`${cfg.bg} ${cfg.border} border-2 rounded-xl p-4 mb-6 animate-fade-in-up relative`} data-testid="river-alert-banner" role="alert">
      <button
        onClick={() => {
          const newDismissed = [...dismissedAlerts, ...activeAlerts.map(a => a.created_at)];
          setDismissedAlerts(newDismissed);
          try { localStorage.setItem('somostorre_dismissed_river_alerts', JSON.stringify(newDismissed)); } catch { /* storage unavailable */ }
        }}
        className="absolute top-3 right-3 p-1 rounded-full hover:bg-black/5 transition-colors"
        aria-label="Cerrar alerta de ríos"
        data-testid="river-alert-dismiss">
        <X size={14} className={cfg.text} aria-hidden="true" />
      </button>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center shrink-0 border ${cfg.border}`}>
          <AlertTriangle size={20} className={cfg.icon} aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <p className={`text-sm font-extrabold ${cfg.text}`} data-testid="river-alert-title">
            {level.key === 'desconocido' ? 'Aviso' : level.label} · Nivel de los ríos
          </p>
          {topAlert.alerts?.map((a, i) => {
            const cur = riverLevel(a.severity);
            const prev = riverLevel(a.prev_severity);
            return (
              <p key={`${a.codigo || a.station}-${i}`} className={`text-xs ${cfg.text} mt-1`} data-testid={`river-alert-detail-${i}`}>
                <span className="font-bold">Río {a.rio}</span> · {a.station}: {a.nivel != null ? `${numEs(a.nivel, 2)} m` : 'nivel sin dato'}{a.tendencia ? ` (${a.tendencia})` : ''}
                {cur.key !== 'desconocido' && (
                  <span className="ml-1">
                    — {prev.key !== 'desconocido' && prev.key !== cur.key ? `pasa de ${prev.label} a ${cur.label}` : cur.label}
                  </span>
                )}
              </p>
            );
          })}
          <a href="https://visor.saichcantabrico.es" target="_blank" rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 text-xs font-bold ${cfg.text} mt-2 hover:underline`} data-testid="river-alert-visor-link">
            Ver el visor del SAIH Cantábrico <ExternalLink size={10} aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  );
}
